/**
 * TT-701 — optimistic liked state that outlives a background revalidate.
 *
 * A heart tap writes one design through POST /api/gallery/liked-ids/delta, but
 * the grid reads liked state from the *published* liked-design-ids.json. That
 * file is rewritten by the same request, yet a background revalidate can still
 * observe the pre-toggle list (in-memory TTL entry, nginx/browser cache, or the
 * IndexedDB bootstrap snapshot), rebuild the index from it, and silently
 * un-fill the heart the staff member just tapped.
 *
 * So every in-flight or confirmed toggle is remembered as an override, is
 * re-applied on top of every rebuilt index and every snapshot we persist, and
 * is forgotten only once the server payload agrees with it — or once the save
 * failed and the UI was reverted.
 *
 * Curated-liked designs never agree: `is_curated_liked` lives in
 * design-library.json and is written by the offline curation scripts, not by
 * the liked-ids write, so unliking one is *only* representable as an override.
 * That is why overrides have no expiry — an expiry would resurrect a heart the
 * staff member deliberately cleared.
 *
 * Deliberately dependency-free so it stays trivially testable.
 */

/**
 * Accept a Map (what the provider holds), pair array, or plain object.
 *
 * The array check must come first: arrays also have `.entries()`, but it yields
 * `[index, value]`, so duck-typing on it turns `[[42, true]]` — the shape
 * `patchBootstrapSnapshotLiked` passes for a single toggle — into id `0`.
 */
function overrideEntries(overrides) {
  if (!overrides) return [];
  if (Array.isArray(overrides)) return overrides;
  if (typeof overrides.entries === "function") return [...overrides.entries()];
  return Object.entries(overrides);
}

function likedIdArray(likedIdsJson) {
  const raw = Array.isArray(likedIdsJson) ? likedIdsJson : likedIdsJson?.ids;
  if (!Array.isArray(raw)) return [];
  return raw.map((id) => Number(id)).filter((n) => Number.isFinite(n));
}

/**
 * Fold overrides into a liked-design-ids.json payload, preserving its shape.
 *
 * Used for the IndexedDB bootstrap snapshot: the snapshot is what paints the
 * grid on the next open, so persisting the raw network payload would show a
 * stale heart before the first revalidate lands.
 *
 * @param {{ids?: Array<number|string>}|Array<number|string>|null|undefined} likedIdsJson
 * @param {Map<string|number, boolean>|Array<[string|number, boolean]>|object} overrides
 * @returns {{ids: number[], total: number}|number[]} same shape as the input
 */
export function likedIdsWithOverrides(likedIdsJson, overrides) {
  const ids = new Set(likedIdArray(likedIdsJson));
  for (const [id, liked] of overrideEntries(overrides)) {
    const n = Number(id);
    if (!Number.isFinite(n)) continue;
    if (liked) ids.add(n);
    else ids.delete(n);
  }
  const next = [...ids].sort((a, b) => a - b);
  if (Array.isArray(likedIdsJson)) return next;
  return { ...(likedIdsJson || {}), ids: next, total: next.length };
}

/**
 * Split overrides into the ones a freshly built index still needs and the ones
 * the server has caught up on.
 *
 * `serverLikedOf(id)` must use the **same predicate the heart renders**
 * (`isDesignLiked`), not the raw liked-id set — otherwise a curated-liked
 * design would look "not yet caught up" forever in one direction and settle
 * wrongly in the other. Returning `null`/`undefined` means the payload no
 * longer carries that design (deleted): nothing is left to protect, so the
 * override is settled rather than kept forever.
 *
 * @param {Map<string|number, boolean>|Array<[string|number, boolean]>|object} overrides
 * @param {(id: string|number) => boolean|null|undefined} serverLikedOf
 * @returns {{apply: Array<[string|number, boolean]>, settled: Array<string|number>}}
 */
export function reconcileLikedOverrides(overrides, serverLikedOf) {
  const apply = [];
  const settled = [];
  for (const [id, liked] of overrideEntries(overrides)) {
    const server = serverLikedOf(id);
    if (server == null) {
      settled.push(id);
      continue;
    }
    if (Boolean(server) === Boolean(liked)) settled.push(id);
    else apply.push([id, Boolean(liked)]);
  }
  return { apply, settled };
}
